import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, C } from '../../constants';
import { formatMsgTime } from '../../utils';
import { subscribeSSE } from '../../sse';
import useWindowSize from '../../hooks/useWindowSize';

export default function ChatBubble({ token, user }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [sending, setSending]   = useState(false);
  const [unread, setUnread]     = useState(0);
  const [vpH, setVpH]           = useState(() => window.visualViewport?.height || window.innerHeight);
  const [vpTop, setVpTop]       = useState(0);
  const lastIDRef  = useRef(0);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const openRef    = useRef(false);
  const fetchRef   = useRef(null);
  const { width }  = useWindowSize();
  const isMobile   = width < 550;

  useEffect(() => { openRef.current = open; }, [open]);

  // Seguir visualViewport para teclado virtual en móvil
  useEffect(() => {
    const vp = window.visualViewport;
    if (!vp) return;
    const update = () => { setVpH(vp.height); setVpTop(vp.offsetTop); };
    vp.addEventListener('resize', update);
    vp.addEventListener('scroll', update);
    return () => { vp.removeEventListener('resize', update); vp.removeEventListener('scroll', update); };
  }, []);

  // Bloquear scroll del body cuando el chat está abierto en móvil
  useEffect(() => {
    if (!isMobile) return;
    if (open) { document.body.style.overflow = 'hidden'; document.body.style.position = 'fixed'; document.body.style.width = '100%'; }
    else { document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; }
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; };
  }, [open, isMobile]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/messages?since=${lastIDRef.current}&limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (!data || data.length === 0) return;
      lastIDRef.current = data[data.length - 1].id;
      setMessages(prev => {
        const existingIDs = new Set(prev.map(m => m.id));
        const newMsgs = data.filter(m => !existingIDs.has(m.id));
        if (newMsgs.length === 0) return prev;
        if (!openRef.current) setUnread(u => u + newMsgs.filter(m => m.sender_id !== user.id).length);
        return [...prev, ...newMsgs];
      });
    } catch {}
  }, [token, user.id]);

  useEffect(() => { fetchRef.current = fetchMessages; }, [fetchMessages]);

  useEffect(() => {
    if (!token) return;
    fetchRef.current?.();
    return subscribeSSE(token, data => {
      try {
        if (data.type === 'connected') { fetchRef.current?.(); return; }
        if (data.type === 'messages_read') {
          setMessages(prev => prev.map(m => m.sender_id === user.id ? { ...m, read: true } : m));
          return;
        }
        if (data.type !== 'message') return;
        fetchRef.current?.();
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          if (!openRef.current) setUnread(u => u + (data.sender_id !== user.id ? 1 : 0));
          return [...prev, data];
        });
        lastIDRef.current = Math.max(lastIDRef.current, data.id);
      } catch {}
    });
  }, [token, user.id]);

  const bubbleScrollRef    = useRef(null);
  const bubbleAtBottomRef  = useRef(true);
  const handleBubbleScroll = () => {
    const el = bubbleScrollRef.current;
    if (!el) return;
    bubbleAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  useEffect(() => {
    if (open && bubbleAtBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const handleOpen = () => {
    setOpen(true); setUnread(0);
    setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'instant' }); inputRef.current?.focus(); }, 80);
    fetch(`${API}/api/chat/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const sendMsg = async () => {
    const content = inputRef.current?.value?.trim();
    if (!content || sending) return;
    inputRef.current.value = '';
    autoResize(inputRef.current);
    setSending(true);
    try {
      await fetch(`${API}/api/chat/send`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content, receiver_id: 0 }) });
      await fetchMessages();
    } catch {}
    setSending(false);
    inputRef.current?.focus();
  };

  const autoResize = el => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 80) + 'px';
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } };

  // Estilos según viewport con soporte de teclado virtual
  const chatStyle = isMobile
    ? { position: 'fixed', top: vpTop + 'px', left: 0, width: '100vw', height: vpH + 'px', borderRadius: 0 }
    : { position: 'fixed', bottom: '72px', right: '16px', width: '320px', height: '460px', borderRadius: '18px' };

  return (
    <div style={{ position: 'fixed', bottom: isMobile ? '76px' : '20px', right: '20px', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
      {open && (
        <div style={{ ...chatStyle, background: C.bgModal, border: `1px solid ${C.accentBorder}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.8)', zIndex: 1001 }}>
          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>T</div>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>Soporte Tlalli</p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>● Responderemos pronto</p>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>

          {/* Mensajes */}
          <div ref={bubbleScrollRef} onScroll={handleBubbleScroll} style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px', scrollbarWidth: 'thin', scrollbarColor: `${C.accentBorder} transparent`, WebkitOverflowScrolling: 'touch' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                <p style={{ fontSize: '32px', marginBottom: '10px' }}>💬</p>
                <p style={{ color: C.textMuted, fontSize: '13px' }}>¿Tienes alguna duda o sugerencia?</p>
                <p style={{ color: C.textDim, fontSize: '12px', marginTop: '4px' }}>Escríbenos, estamos para ayudarte</p>
              </div>
            )}
            {messages.map(m => {
              const isMe = m.sender_id === user.id;
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                  {!isMe && <p style={{ color: C.textDim, fontSize: '10px', marginBottom: '3px', paddingLeft: '4px' }}>{m.sender_role === 'admin' ? '🛡 Soporte' : m.sender_name}</p>}
                  <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: isMe ? '14px 14px 3px 14px' : '14px 14px 14px 3px', background: isMe ? `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})` : 'rgba(255,255,255,0.07)', border: isMe ? 'none' : `1px solid ${C.accentBorder}` }}>
                    <p style={{ color: isMe ? '#fff' : C.text, fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>{m.content}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                    <p style={{ color: C.textDim, fontSize: '10px' }}>{formatMsgTime(m.created_at)}</p>
                    {isMe && <span style={{ fontSize: '12px', color: m.read ? '#5dade2' : C.textDim, lineHeight: 1, fontWeight: '600' }}>{m.read ? '✓✓' : '✓'}</span>}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input — pegado al fondo, encima del teclado virtual */}
          <div style={{ padding: '10px 12px', paddingBottom: 'max(10px, env(safe-area-inset-bottom))', borderTop: `1px solid ${C.accentBorder}`, display: 'flex', gap: '8px', alignItems: 'flex-end', background: C.bgModal, flexShrink: 0 }}>
            <textarea
              ref={inputRef}
              onKeyDown={handleKey}
              onInput={e => autoResize(e.target)}
              placeholder="Escribe un mensaje..."
              rows={1}
              style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.accentBorder}`, borderRadius: '10px', padding: '9px 12px', color: C.text, fontSize: '13px', outline: 'none', resize: 'none', fontFamily: 'Inter, sans-serif', lineHeight: '1.4', height: '38px', maxHeight: '80px', overflow: 'auto', scrollbarWidth: 'none' }}
            />
            <button onClick={sendMsg} disabled={sending} style={{ width: '38px', height: '38px', background: `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: sending ? 0.6 : 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* FAB — solo visible cuando el chat está cerrado */}
      {!open && (
        <button onClick={handleOpen} style={{ width: '52px', height: '52px', background: `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px rgba(201,113,74,0.5)`, position: 'relative', transition: 'transform 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
          {unread > 0 && <div style={{ position: 'absolute', top: '-3px', right: '-3px', width: '18px', height: '18px', background: '#e74c3c', borderRadius: '50%', fontSize: '10px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', border: `2px solid ${C.bg}` }}>{unread > 9 ? '9+' : unread}</div>}
        </button>
      )}
    </div>
  );
}
